/**
 * DocuWare Rechnungseingang (/docuware-invoices) — read path.
 *
 * Every field is what SQL returned, unshifted: dates arrive as strings
 * (CONVERT 23 / 126) and are formatted with utils/format.ts. Never `toISOString()` —
 * the UTC shift moves a CET date to the previous day.
 *
 * Timestamps carry an explicit `Z` from the API and are rendered with formatDateTimeCH;
 * `invoiceDate` is a calendar day and gets no zone at all.
 */
import { toUtcInstant } from '../utils/format';

/** Companies are DocuWare organisations, not just labels — separate hosts and id spaces. */
export type DocuWareCompanyCode = 'dch' | 'dbi' | (string & {});

export interface DocuWareInvoiceRow {
    id: string;
    companyCode: DocuWareCompanyCode;
    fileCabinetId: string | null;
    /** DWDOCID. Unique per file cabinet only — string, because it is a BIGINT. */
    docId: string | null;
    storedAt: string | null;
    modifiedAt: string | null;
    supplier: string | null;
    invoiceNumber: string | null;
    invoiceDate: string | null;
    amount: number | null;
    currency: string | null;
    /**
     * ALL raw index fields as DocuWare delivered them. The typed columns above are a
     * projection whose mapping lives in config and may go NULL when iKlaro renames
     * fields (Oct 2026) — this stays complete either way, and it is what the expandable
     * row renders.
     */
    indexFields: Record<string, unknown> | null;
    /** Set only when the stored JSON could not be parsed — then this is the verbatim text. */
    indexFieldsRaw: string | null;
    /**
     * Spelled-out names for the login blobs in indexFields, keyed by the DocuWare field name.
     * Only resolved entries are present — an absent field means the raw value is shown, which
     * is deliberate: a wrong name on an approval trail is worse than an unresolved one.
     */
    resolvedUsers: Record<string, ResolvedDocuWareUser>;
    source: 'webhook' | 'rest' | 'seed' | (string & {});
    /** When WE first saw the document. Drives the NEU badge; independent of DocuWare. */
    firstSeenAt: string | null;
    lastSeenAt: string | null;
    /** Built server-side from that company's config. null = we cannot build an honest link. */
    documentUrl: string | null;
    /** false = the URL template is still a guess and the UI must say so. */
    documentUrlVerified: boolean;
    baseUrl: string | null;

    /** Lifted out of indexFields into a column so it can be filtered and faceted. */
    documentType: string | null;
    invoiceCategory: string | null;
    /** dbi only — dch's search dialog does not project WORKFLOW_STATUS. NULL is not a defect. */
    workflowStatus: string | null;

    // --- BC posting, from [Reporting].[DocuWareBcLink] -------------------------------
    /** The posting document ("Buchungsbeleg"). null = not posted yet, or not matchable. */
    bcDocumentNo: string | null;
    bcPostingDate: string | null;
    /** BC's own vendor-ledger type ('Rechnung' / 'Gutschrift'). Shown, never used for links. */
    bcDocumentType: string | null;
    /**
     * WHERE the document is, which is what actually decides the BC page: 'invoice' and
     * 'creditmemo' have a card, 'ledger' exists only as a vendor ledger entry (two thirds of
     * dch — journal postings). null = the matcher has not classified it, so no link.
     */
    bcDocumentKind: 'invoice' | 'creditmemo' | 'ledger' | null;
    bcVendorNo: string | null;
    bcAmount: number | null;
    bcCurrency: string | null;
    matchMethod: DocuWareMatchMethod | null;
    /** 100 both keys agree · 70 one key · 40 keys disagree · 0 nothing found. */
    matchConfidence: number | null;
    /** null = not applicable (no posting found), not "false". */
    amountAgrees: boolean | null;
    vendorAgrees: boolean | null;
    /** DocuWare documents sharing this one BC posting. > 1 means the amounts cannot match. */
    sharedDocuments: number;
    /**
     * BC postings this ONE document became (migration 192). > 1 is a collective invoice:
     * accounting split it and `bcDocumentNo` shows only the first of them, so the posted
     * amount is a PART of the document's and cannot agree with it.
     */
    postingCount: number;
    /**
     * The posting numbers themselves, primary first, e.g. '842036, 842037'.
     *
     * `postingCount` can only put a caveat on the screen; this is what stops the row from
     * showing '842036' for a document that is 842036 AND 842037 - the silent narrowing P1
     * removed from the data and P2 removes from the display.
     */
    bcPostings: string | null;
}

/** A DocuWare login resolved to a person (or to a label, for the technical accounts). */
export interface ResolvedDocuWareUser {
    /** "Vorname Nachname", or a curated label like "Scan import". */
    display: string;
    email: string | null;
    staffId: string | null;
    /** false once the person has left. Their history still carries their name. */
    isActive: boolean;
    source: 'alias' | 'directory';
}

/** How the posting was identified. See api .../shared/bc-match.ts for what each one costs. */
export type DocuWareMatchMethod = 'both' | 'belegnummer' | 'externaldoc' | 'conflict' | 'none';

export interface DocuWareCompanySummary {
    companyCode: DocuWareCompanyCode;
    documents: number;
    newest: string | null;
}

export interface DocuWareFacetValue {
    value: string;
    documents: number;
}

/**
 * The values that actually occur, narrowed to the selected company.
 *
 * Server-supplied rather than hard-coded because the two organisations use different
 * vocabularies: dbi files Waren / Spedition / Allgemein, dch Warenrechnung / Sonstige
 * Rechnung. An empty list is meaningful — its control hides itself, which is how
 * "workflow status" disappears for dch without a company check anywhere in the UI.
 */
export interface DocuWareFacets {
    documentType: DocuWareFacetValue[];
    invoiceCategory: DocuWareFacetValue[];
    workflowStatus: DocuWareFacetValue[];
    currency: DocuWareFacetValue[];
}

export interface DocuWareInvoiceListResult {
    items: DocuWareInvoiceRow[];
    /** Companies that actually have rows — the company filter renders only when > 1. */
    companies: DocuWareCompanySummary[];
    facets: DocuWareFacets;
    /** When the ingest last finished successfully. The page must be able to say how old it is. */
    lastImport: string | null;
    lastImportCompany: string | null;
    total: number;
}

/** Posted / still open in DocuWare / everything. */
export type DocuWarePostingStatus = 'all' | 'open' | 'posted';

export interface DocuWareInvoiceFilters {
    companyCode?: string;
    search?: string;
    postingStatus?: DocuWarePostingStatus;
    storedFrom?: string;
    storedTo?: string;
    invoiceDateFrom?: string;
    invoiceDateTo?: string;
    documentType?: string;
    invoiceCategory?: string;
    workflowStatus?: string;
    currency?: string;
    amountMin?: string;
    amountMax?: string;
}

export type DocuWareSortField =
    | 'company' | 'stored' | 'received' | 'supplier' | 'invoiceNumber' | 'invoiceDate'
    | 'amount' | 'currency' | 'documentType' | 'invoiceCategory' | 'workflowStatus'
    | 'bcDocumentNo' | 'bcPostingDate';

/** The three honest link states from the plan. Never invent a fourth. */
export type DocuWareLinkState = 'verified' | 'unverified' | 'none';

/**
 * Structural, not tied to DocuWareInvoiceRow: the order-check rows carry the same three
 * server-decided fields and must render the same three states, from the same function.
 */
export interface DocuWareLinkFields {
    documentUrl: string | null;
    documentUrlVerified: boolean;
    baseUrl: string | null;
    docId: string | number | null;
}

export function docuWareLinkState(row: DocuWareLinkFields): DocuWareLinkState {
    if (!row.documentUrl) return 'none';
    return row.documentUrlVerified ? 'verified' : 'unverified';
}

/**
 * NEW badge window: arrived within the last 24 hours.
 *
 * Shares toUtcInstant with the formatters. Before, this function appended its own 'Z' while
 * formatDateTime did not — so the same string on the same row was read as two different
 * instants, and the badge was right while the column was two hours wrong.
 */
export function isNewArrival(firstSeenAt: string | null): boolean {
    const normalized = toUtcInstant(firstSeenAt);
    if (!normalized) return false;
    const seen = new Date(normalized).getTime();
    if (Number.isNaN(seen)) return false;
    return Date.now() - seen < 24 * 60 * 60 * 1000;
}

/**
 * Is the feed stale enough to say so out loud?
 *
 * On 2026-08-20 the timer failed on every tick for sixteen hours — a key rotation had reached
 * the SWA but not the timer host, so DocuWare answered 401. The ingest catches that per company
 * and logs it, which means no run row and no Failed status: the page showed a quiet feed and a
 * dead one identically. This is the cheapest honest fix — no mail, no second timer, just
 * refusing to present silence as calm.
 *
 * The schedule is part of the judgement. The ingest runs hh:05, 05:00-17:00 UTC, Mon-Fri, so
 * a Saturday or a 22:00 gap is CORRECT and warning about it would train people to ignore the
 * warning. The 30-minute grace after the window opens keeps Monday 05:05 from firing before
 * the week's first tick has had a chance to land.
 */
const STALE_AFTER_HOURS = 3;   // hourly schedule, so this is three missed ticks, not one blip

export interface DocuWareFeedState {
    /** Hours since the last successful import; null when there has never been one. */
    ageHours: number | null;
    /** True only when a tick was genuinely due and did not arrive. */
    stale: boolean;
}

export function docuWareFeedState(lastImport: string | null, now: Date = new Date()): DocuWareFeedState {
    const normalized = toUtcInstant(lastImport);
    const then = normalized ? new Date(normalized).getTime() : NaN;
    const ageHours = Number.isNaN(then) ? null : (now.getTime() - then) / 3_600_000;

    // UTC throughout: the cron is expressed in UTC and the viewer's zone is irrelevant to
    // whether a run was due.
    const day = now.getUTCDay();                 // 0 = Sunday
    const hour = now.getUTCHours();
    const minute = now.getUTCMinutes();
    const withinWeek = day >= 1 && day <= 5;
    const afterFirstTick = hour > 5 || (hour === 5 && minute >= 35);
    const tickWasDue = withinWeek && afterFirstTick && hour <= 17;

    return { ageHours, stale: tickWasDue && (ageHours === null || ageHours > STALE_AFTER_HOURS) };
}

/**
 * Why a posting link deserves a caveat, or null when it does not.
 *
 * `sharedDocuments > 1` is checked FIRST and is not a warning at all: one BC posting
 * routinely bundles several invoices, and then the per-document amount simply cannot equal
 * the posting's. Reporting that as "amount mismatch" would flag hundreds of correct links.
 *
 * `postingCount > 1` is the mirror image and arrived with migration 192: one invoice split
 * into several postings, of which `bcDocumentNo` names the first. Without this branch the
 * amount check below would call every collective invoice a mismatch — 25 documents that were
 * silently unlinked before and are correctly linked now.
 */
export function matchCaveat(row: DocuWareInvoiceRow): string | null {
    if (!row.bcDocumentNo) return null;
    if (row.matchMethod === 'conflict') {
        return 'DocuWare and Business Central name different posting documents for this invoice.';
    }
    if (row.sharedDocuments > 1) {
        return `This posting covers ${row.sharedDocuments} DocuWare documents, so its amount is the total.`;
    }
    if (row.postingCount > 1) {
        return `This invoice was posted as ${row.postingCount} documents; ${row.bcDocumentNo} is the first of them.`;
    }
    if (row.amountAgrees === false) {
        return 'The posted amount differs from the amount on the document.';
    }
    if (row.matchMethod === 'belegnummer') {
        return 'Taken from DocuWare’s own reference; no matching Business Central entry confirmed it.';
    }
    if (row.matchMethod === 'externaldoc') {
        return 'Matched on the supplier invoice number; DocuWare carries no posting reference.';
    }
    return null;
}

export const COMPANY_LABELS: Record<string, string> = {
    dch: 'Dolder AG (dch)',
    dbi: 'Dolder-Bigler (dbi)',
    dit: 'Dolder Massara',
};

export function companyLabel(code: string): string {
    return COMPANY_LABELS[code] ?? code;
}

/**
 * How many sections the archived document holds, read from the row's own index fields.
 *
 * The list response carries no separate count: `DWSECTIONCOUNT` travels inside `indexFields`,
 * the same value the PDF endpoint reads (`SectionCount` in `document-pdf.ts`). Missing or
 * unparseable counts as 1 there (`ISNULL(TRY_CAST(...), 1)`), so it does here, or the window
 * and the endpoint would disagree about whether section 1 exists.
 *
 * **0 is a real answer**: 49 dit documents carry `DWSECTIONCOUNT = 0` and the endpoint answers
 * 404 NO_SUCH_SECTION for every section of them. The caller hides the PDF button for 0 rather
 * than offering a window that can only fail.
 */
export function docuWareSectionCount(row: Pick<DocuWareInvoiceRow, 'indexFields'>): number {
    const raw = row.indexFields?.DWSECTIONCOUNT;
    const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw.trim()) : NaN;
    if (raw === undefined || raw === null || raw === '' || !Number.isInteger(n)) return 1;
    return Math.max(0, n);
}
